<template>
	<div
		v-if="athletePublicProfile"
		class="exchange-public-athlete-profile h-100 w-100"
	>
		<NavigationMenu />
		<VContainer
			class="exchange-public-athlete-profile__container d-flex text-white flex-column"
		>
			<template v-if="athletePublicProfile">
				<VRow class="exchange-public-athlete-profile__profile-picture w-100 flex-md-row-reverse justify-md-space-between align-md-center">
					<VCol
						class="d-flex justify-md-end"
						cols="12"
						md="2"
					>
						<div class="position-relative">
							<CommonAvatar
								:src="athletePublicProfile.profileImage"
								:alt="athletePublicProfile.fullName"
								:size="mobile ? 96 : 143"
							/>
							<VChip
								v-if="athletePublicProfile.schoolLogo"
								class="exchange-public-athlete-profile__school-logo-chip px-4"
								color="white"
							>
								<VImg
									:alt="athletePublicProfile.schoolName"
									:src="athletePublicProfile.schoolLogo"
									height="24"
									width="24"
								/>
							</VChip>
						</div>
					</VCol>
					<VCol
						class="exchange-public-athlete-profile__details justify-end"
						cols="12"
						md="10"
					>
						<div class="exchange-public-athlete-profile__details-exchange-title font-weight-medium">
							{{ t('exchangePublicProfile.title', { exchangeTitle: athletePublicProfile.localExchange.title }) }}
						</div>
						<div class="exchange-public-athlete-profile__details-name font-weight-bold">
							{{ athletePublicProfile.fullName }}
						</div>
						<div
							v-if="!isEmpty(athletePublicProfile.bio)"
							class="exchange-public-athlete-profile__details-bio mb-4"
						>
							{{ athletePublicProfile.bio }}
						</div>
						<ul
							v-if="!isEmpty(athletePublicProfile.socialProfiles)"
							class="exchange-public-athlete-profile__details-social-links reset-list d-flex ga-4 align-center"
						>
							<VBtn
								v-for="(socialProfile, index) in athletePublicProfile.socialProfiles"
								:key="index"
								class="exchange-public-athlete-profile__details-social-links-item"
								:aria-label="socialProfile.platform?.name"
								:color="socialProfile.platform?.color"
								:icon="socialProfile.platform?.icon"
								:href="socialProfile.url"
								target="_blank"
								size="small"
							/>
						</ul>
					</VCol>
				</VRow>
				<VRow
					v-if="athletePublicProfile.rateCardsCount > 0"
					class="mt-6 mt-md-16 w-100"
				>
					<VCol
						v-for="(rateCard, index) in athletePublicProfile.rateCards"
						:key="index"
						cols="12"
						md="4"
						lg="3"
					>
						<AthleteRateCard
							:athlete-uuid="athletePublicProfile.uuid"
							:local-exchange-url="athletePublicProfile.localExchange.url"
							:rate-card="rateCard"
						/>
					</VCol>
				</VRow>
				<VRow>
					<VCol
						class="mt-2 text-center"
						cols="12"
					>
						<VBtnSecondary
							:active="false"
							class="text-decoration-none"
							data-testid="buttonRegisterNow"
							:href="`${athletePublicProfile.localExchange.url}/registration?athleteUuid=${athletePublicProfile.uuid}`"
							size="x-large"
							theme="dark"
						>
							{{ t('workWithMe') }}
						</VBtnSecondary>
					</VCol>
				</VRow>
			</template>
		</VContainer>
	</div>
</template>

<script lang="ts" setup>
import { computed, onMounted } from 'vue';
import NavigationMenu from '@/components/exchange-athlete-public-profile/NavigationMenu.vue';
import { CommonAvatar } from '@teamworksdev/vuetify';
import AthleteRateCard from '@/components/exchange-athlete-public-profile/AthleteRateCard.vue';
import { useI18n } from 'vue-i18n';
import { setTitleTag } from '@/composables/setTitleTag';
import { useExchangeAthletesService } from '@/api/exchangeAthletesService';
import { isApiError } from '@/api/utilities/provider';
import { useAsyncState } from '@vueuse/core';
import { useDisplay } from 'vuetify';
import { useRouter } from 'vue-router';
import { isEmpty } from 'lodash-es';

const props = defineProps<{
	username: string;
}>();
const { t } = useI18n();
const { mobile } = useDisplay();
const exchangeAthletesService = useExchangeAthletesService({ bypassErrorToast: true });

const router = useRouter();

const {
	state: athletePublicProfile,
	execute: getExchangeAthletePublicProfile,
} = useAsyncState(
	async () => {
		return await exchangeAthletesService.getExchangeAthletePublicProfile(props.username);
	},
	undefined,
	{ immediate: false,
		onSuccess(data) {
			if (data) router.replace({ name: 'AthletePublicProfile', params: { username: data.profileName } });
		},
		onError(error) {
			if (isApiError(error)) {
				switch (error.status) {
					case 404:
						router.replace({ name: 'NotFound' });
						break;
					default:
						console.error(error);
						break;
				}
			} else {
				console.error(error);
			}
		},
	},
);

setTitleTag(computed(() => athletePublicProfile?.value?.fullName));

onMounted(() => {
	getExchangeAthletePublicProfile();
});
</script>

<style lang="scss" scoped>
@use "sass:map";
@use "@/styles/settings.scss";

 .exchange-public-athlete-profile {
	background-image: url(@/assets/bg__exchange-athlete-public-profile.jpg);
	background-color: #000;
	background-size: cover;
	background-position: center;

	&__container {
		padding-top: 99px;

		@media screen and (min-width: map.get(settings.$grid-breakpoints, "md")) {
			padding-top: 164px;
		}
	}
	&__school-logo-chip {
		position: absolute;
		bottom: -12px;
		left: 50%;
		transform: translateX(-50%);
	}
	&__details {
		&-name {
			font-size: 3rem;

			@media screen and (min-width: map.get(settings.$grid-breakpoints, "md")) {
				font-size: 5rem;
			}
		}
	}
}
</style>
